import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, ShieldCheckIcon, StarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { BrandedQuoteForm } from './BrandedQuoteForm';
interface BrandedLandingPageProps {
  onClose: () => void;
}
export function BrandedLandingPage({
  onClose
}: BrandedLandingPageProps) {
  const [showForm, setShowForm] = useState(false);
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip) {
      setShowForm(true);
    }
  };
  if (showForm) {
    return <BrandedQuoteForm initialZip={zipCode} onClose={onClose} data-id="element-33" />;
  }
  const ChampionHeader = () => <header className="bg-white py-4 px-6 md:px-12 flex justify-between items-center shadow-sm relative z-20" data-id="element-34">
      <div className="flex flex-col" data-id="element-35">
        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-[-4px]" data-id="element-36">
          Partner
        </span>
        <div className="flex items-baseline gap-2" data-id="element-37">
          <span className="text-2xl md:text-4xl font-black text-[#CC0000] tracking-tighter italic" data-id="element-38">
            CHAMPION
          </span>
        </div>
        <span className="text-[10px] md:text-sm font-semibold text-[#003DA5]" data-id="element-39">
          Windows • Sunrooms • Home Exteriors
        </span>
      </div>
      <button onClick={onClose} className="text-sm font-medium text-gray-500 hover:text-[#CC0000] transition-colors" data-id="element-40">
        Back to Directory
      </button>
    </header>;
  return <div className="min-h-screen bg-[#F5F5F5] font-sans" data-id="element-41">
      <ChampionHeader data-id="element-42" />

      {/* Special Offer Banner */}
      <section className="bg-[#003DA5] py-6 md:py-8 border-t border-blue-800" data-id="element-43">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-44">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-12" data-id="element-45">
            <div className="flex-1" data-id="element-46">
              <div className="inline-block bg-[#FFC107] text-black font-bold text-sm px-3 py-1 rounded-sm mb-3" data-id="element-47">
                Special Offer
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight" data-id="element-48">
                Buy 2 Windows, Get 2 FREE*
              </h2>
            </div>
            <div className="hidden md:block w-px h-16 bg-white/20" data-id="element-49"></div>
            <div className="flex-1" data-id="element-50">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-2" data-id="element-51">
                Plus, Pay Nothing for a Year**
              </h3>
              <p className="text-blue-100 text-sm md:text-base leading-relaxed" data-id="element-52">
                Interest is billed during promo period but will be waived if the
                amount financed is paid in full before promo period expires.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative pt-16 pb-24 md:pt-24 md:pb-32 overflow-hidden" data-id="element-53">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0" data-id="element-54">
          <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80" alt="Beautiful living room with large windows" className="w-full h-full object-cover" data-id="element-55" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" data-id="element-56"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-57">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-58">
            {/* Left Content */}
            <motion.div className="flex-1 text-white" initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-59">
              <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-4 leading-tight drop-shadow-lg" data-id="element-60">
                Buy 2 Windows,
                <br data-id="element-61" />
                Get 2 FREE*
              </h1>
              <p className="text-xl md:text-2xl font-medium text-gray-200 drop-shadow-md" data-id="element-62">
                Plus, Pay Nothing for a Year**
              </p>
            </motion.div>

            {/* Right Form Card */}
            <motion.div className="w-full max-w-md" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-63">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" data-id="element-64">
                <h2 className="text-2xl font-bold text-[#003DA5] text-center mb-6" data-id="element-65">
                  Enter Your Zip Code To Start
                </h2>

                <div className="relative mb-6" data-id="element-66">
                  <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Zip Code" className="w-full h-16 px-6 text-2xl font-bold text-center bg-white border-2 border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-[#003DA5] focus:ring-1 focus:ring-[#003DA5] transition-colors" maxLength={5} data-id="element-67" />
                  {isValidZip && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" data-id="element-68">
                      <CheckCircle2Icon className="w-8 h-8" data-id="element-69" />
                    </div>}
                </div>

                <button onClick={handleContinue} className={`w-full h-16 rounded-xl font-bold text-xl text-white transition-all shadow-lg ${isValidZip ? 'bg-[#CC0000] hover:bg-red-800 shadow-red-500/30 hover:shadow-red-500/40 cursor-pointer' : 'bg-gray-300 cursor-not-allowed shadow-none'}`} data-id="element-70">
                  Continue
                </button>

                {/* Trust Badges */}
                <div className="mt-8 flex items-center justify-center gap-6" data-id="element-71">
                  <div className="flex items-center gap-2 bg-yellow-50 px-3 py-1.5 rounded-lg border border-yellow-200" data-id="element-72">
                    <ShieldCheckIcon className="w-5 h-5 text-yellow-600" data-id="element-73" />
                    <span className="text-xs font-bold text-yellow-800 uppercase tracking-tight" data-id="element-74">
                      Lifetime Warranty
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200" data-id="element-75">
                    <div className="font-black text-[#003DA5] text-sm" data-id="element-76">BBB</div>
                    <span className="text-xs font-bold text-blue-800 uppercase tracking-tight" data-id="element-77">
                      A+ Rating
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Red Accent Bar */}
      <div className="w-full h-3 bg-[#CC0000]" data-id="element-78"></div>

      {/* What to Expect Section */}
      <section className="py-20 bg-white" data-id="element-79">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-80">
          <div className="text-center mb-16" data-id="element-81">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-82">
              What to Expect at Your Free In-Home Estimate
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center" data-id="element-83">
            {[{
            title: 'Find The Right Style',
            desc: "Your Champion representative will walk you through window options, custom styles and colors, and warranties. They'll also help you compare the value of Champion.",
            img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=400&fit=crop'
          }, {
            title: 'Review Price & Financing',
            desc: "You'll review all current promotions, calculate your estimated monthly payment and energy bill savings, and determine the best financing options for you.",
            img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=400&fit=crop'
          }, {
            title: 'Same Day Quote',
            desc: "You'll get an exact, no-obligation quote for both your new windows and installation, and you'll discuss next steps in the window replacement process.",
            img: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&h=400&fit=crop'
          }].map((item, idx) => <motion.div key={idx} className="flex flex-col items-center" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2,
            duration: 0.5
          }} data-id="element-84">
                <div className="w-48 h-48 rounded-full overflow-hidden mb-6 shadow-lg border-4 border-white" data-id="element-85">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" data-id="element-86" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-87">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-88">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-[#F5F5F5]" data-id="element-89">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-90">
          <div className="text-center mb-12" data-id="element-91">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-92">
              See What Our Customers Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-93">
            {[{
            name: 'Sarah M.',
            location: 'Columbus, OH',
            text: "The installation crew was professional, clean, and fast. Our new windows look amazing and we've already noticed a difference in our energy bills."
          }, {
            name: 'Robert & Linda T.',
            location: 'Denver, CO',
            text: 'From the initial consultation to the final walkthrough, Champion exceeded our expectations. The quality of the sunroom is outstanding.'
          }, {
            name: 'James W.',
            location: 'Atlanta, GA',
            text: "We replaced 12 windows and a sliding door. The 'Buy 2 Get 2' offer saved us thousands, and the financing made it incredibly affordable."
          }].map((review, idx) => <motion.div key={idx} className="bg-white p-8 rounded-xl shadow-sm border border-gray-200" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} data-id="element-94">
                <div className="flex text-yellow-400 mb-4" data-id="element-95">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5 fill-current" data-id="element-96" />)}
                </div>
                <p className="text-gray-600 mb-6 italic" data-id="element-97">"{review.text}"</p>
                <div data-id="element-98">
                  <p className="font-bold text-gray-900" data-id="element-99">{review.name}</p>
                  <p className="text-sm text-gray-500" data-id="element-100">{review.location}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#003DA5] py-16" data-id="element-101">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-102">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-103">
            {/* Logo & Copyright */}
            <div className="flex flex-col gap-8" data-id="element-104">
              <div className="flex flex-col" data-id="element-105">
                <span className="text-[10px] font-bold text-white/70 uppercase tracking-wider mb-[-4px]" data-id="element-106">
                  Partner
                </span>
                <div className="flex items-baseline gap-2" data-id="element-107">
                  <span className="text-3xl font-black text-white tracking-tighter italic" data-id="element-108">
                    CHAMPION
                  </span>
                </div>
                <span className="text-[10px] font-semibold text-white/90" data-id="element-109">
                  Windows • Sunrooms • Home Exteriors
                </span>
              </div>
              <p className="text-xs text-white/80 font-medium" data-id="element-110">
                © {new Date().getFullYear()} www.championwindowquotes.com. All
                Rights Reserved.
              </p>
            </div>

            {/* Links */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-16" data-id="element-111">
              <div className="flex flex-col gap-4" data-id="element-112">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-113">
                  Home
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-114">
                  Privacy Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-115">
                  Terms Of Use
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-116">
                  Cookie Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-117">
                  Vulnerability Policy
                </a>
              </div>
              <div className="flex flex-col gap-4" data-id="element-118">
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-119">
                  Accessibility Policy
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-120">
                  Do Not Sell or Share My Personal Information
                </a>
                <a href="#" className="text-xs text-white hover:underline font-medium" data-id="element-121">
                  Limit the Use of Sensitive Personal Information
                </a>
              </div>
            </div>
          </div>

          {/* Disclaimers */}
          <div className="border-t border-white/20 pt-8 flex flex-col gap-6" data-id="element-122">
            <p className="text-[10px] text-white/60 leading-relaxed text-justify" data-id="element-123">
              *Buy 2 Windows, Get 2 Free applies to Champion's standard window
              pricing. Minimum purchase of 4 windows required. Discount applies
              to every other window purchased. **Subject to credit approval.
              Interest is billed during the promotional period, but all interest
              is waived if the purchase amount is paid in full within 12 months.
              Valid on new orders only. Cannot be combined with other offers.
              See representative for details.
            </p>
            <p className="text-[10px] text-white/60 leading-relaxed text-justify" data-id="element-124">
              **Plan 2521. Subject to credit approval. Rates range from 15.85% -
              21.19% APR (interest rates range from 17.99% - 24.99%). Only
              well-qualified applicants will receive an APR of 15.85%; some
              applicants may not qualify. Loan amount and rate will vary based
              on your income and creditworthiness. 12 month promotional period
              during which interest is billed but will be waived if the amount
              financed is paid in full before Promo Period expires. Monthly
              payments are not required during the Promo Period. Any unpaid
              balance and amounts owed after Promo Period will be paid over 84
              monthly payments.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}